import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Challenge } from '../../store/online-store';
import { useOnlineStore } from '../../store/online-store';
import ChallengeDetails from './ChallengeDetails';
import { isGuestUsername } from '../../utils/guest';
import { getRatingClass, isDisplayableRating } from '../../utils/rating';
import { log } from '../../utils/logger';
import './OutgoingChallengeModal.css';
// `.player-rating` (e le sue classi di fascia) e' globale ma dichiarata nel
// foglio della lista dei collegati, che qui non e' montata: senza questo import
// il numero uscirebbe senza la sua pillola colorata.
import './OnlinePlayersList.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface OutgoingChallengeModalProps {
  challenge: Challenge;
  onCancel: () => void;
}

function OutgoingChallengeModal({ challenge, onCancel }: OutgoingChallengeModalProps) {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(() =>
    Math.max(0, Math.floor((challenge.expiresAt - Date.now()) / 1000))
  );

  // Log on mount
  useEffect(() => {
    log.info('OutgoingChallengeModal', 'Shown', { username: challenge.toUsername });
  }, [challenge.toUsername]);

  // Derive status message from challenge.status
  const isDeclined = challenge.status === 'declined';
  const isExpiredByTime = timeLeft === 0 && !isDeclined;

  const statusMessage = isDeclined
    ? t('online.challenge.declined')
    : isExpiredByTime
      ? t('online.challenge.expired')
      : null;

  // Calculate time left until challenge expires
  useEffect(() => {
    const updateTime = () => {
      const remaining = Math.max(0, Math.floor((challenge.expiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [challenge.expiresAt]);

  // Auto-clear when expired by time (server didn't respond)
  useEffect(() => {
    if (isExpiredByTime) {
      log.info('OutgoingChallengeModal', 'Challenge expired');
      const timeout = setTimeout(() => {
        useOnlineStore.getState().setOutgoingChallenge(null);
      }, 2000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isExpiredByTime]);

  const riferimentoModale = useModaleAccessibile<HTMLDivElement>(true);

  return (
    <div className="modal-overlay">
      {/*
        * Dialogo dichiarato + fuoco governato. Nessun Escape: da qui si esce
        * annullando la sfida (o lasciandola scadere), e far chiudere la finestra
        * con un tasto senza annullare lascerebbe una sfida in volo che nessuno
        * vede più.
        */}
      <div
        ref={riferimentoModale}
        className={`challenge-modal outgoing ${statusMessage ? 'status-message' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="outgoing-challenge-titolo"
        tabIndex={-1}
      >
        <div className="challenge-header">
          <h2 id="outgoing-challenge-titolo">
            {statusMessage || t('online.challenge.waitingForResponse', { username: challenge.toUsername })}
          </h2>
          {!statusMessage && <div className="timer-badge">{timeLeft}s</div>}
        </div>

        <div className="challenger-info">
          <span className="challenger-name">{challenge.toUsername}</span>
          {/* Il punteggio di chi si sta sfidando, in QUESTA variante a QUESTA
              cadenza: il server lo risolve dalla colonna giusta. Niente numero
              per un ospite, per un bot, o per una partita senza orologio —
              `isDisplayableRating` e' la guardia di casa contro gli zeri e i
              `?? 0` inventati a monte. */}
          {isDisplayableRating(challenge.toRating) && (
            <span className={`player-rating ${getRatingClass(challenge.toRating).toLowerCase()}`}>
              {challenge.toRating}
            </span>
          )}
        </div>

        <ChallengeDetails
          variant={challenge.variant.toString()}
          timeControl={challenge.timeControl}
          color={challenge.color}
          matchType={challenge.matchType}
          rated={challenge.rated}
          gameMode={challenge.gameMode}
          opening={challenge.opening}
          legs={challenge.legs}
          /* Chi si sta sfidando lo sappiamo per nome, e il prefisso `Guest_` è
             riservato lato server a ospiti e bot: è l'unico segnale che una sfida
             in volo porta con sé. `toRating` a questa domanda NON risponde — è
             `null` anche per un iscritto che non ha mai giocato quella
             combinazione. Così la riga dice «Valutata» o «Amichevole», che è
             quello che succederà, invece di rimandare la domanda a chi guarda. */
          opponent={{ isGuest: isGuestUsername(challenge.toUsername) }}
        />

        {!statusMessage && (
          <>
            <div className="outgoing-spinner-container">
              <div className="spinner" />
            </div>
            <div className="challenge-actions">
              <button
                className="decline-btn"
                onClick={() => { log.info('OutgoingChallengeModal', 'Cancel clicked'); onCancel(); }}
              >
                {t('online.challenge.cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default OutgoingChallengeModal;
